import React, { useCallback, useEffect, useMemo, useState } from "react";
import { IonContent, IonHeader, IonIcon, IonPage, IonToolbar } from "@ionic/react";
import {
  checkmarkCircleOutline,
  copyOutline,
  lockClosedOutline,
  logoFacebook,
  refreshOutline,
  trashOutline,
  closeCircleOutline,
} from "ionicons/icons";
import "./CityUpdates.css";
import "./UpdatesPortal.css";

const FUNCTIONS_BASE = "https://us-central1-bacolod-ai-assistant.cloudfunctions.net";
const UPDATES_ADMIN_KEY_STORAGE = "bacolodUpdatesAdminKey";

type InviteRecord = {
  token: string;
  label: string;
  status: string;
  createdAtMs: number;
  expiresAtMs: number | null;
  useCount: number;
  connectUrl: string;
};

type ManagedPage = {
  pageId: string;
  pageName: string;
  category: string | null;
  pictureUrl: string | null;
  status: string;
  approvalStatus: string;
  inviteLabel: string | null;
  submittedByName: string | null;
  submittedAt: string | null;
  approvedAt: string | null;
  lastSyncStatus: string | null;
  lastSyncError: string | null;
  lastSyncAt: string | null;
};

const apiUrl = (name: string) => `${FUNCTIONS_BASE}/${name}`;

const formatTimestamp = (value?: string | null) => {
  if (!value) return "Just now";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Just now";

  return new Intl.DateTimeFormat("en-PH", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
};

const UpdatesAdmin: React.FC = () => {
  const [adminKey, setAdminKey] = useState("");
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isActing, setIsActing] = useState(false);
  const [copiedInviteToken, setCopiedInviteToken] = useState("");
  const [invites, setInvites] = useState<InviteRecord[]>([]);
  const [pendingPages, setPendingPages] = useState<ManagedPage[]>([]);
  const [approvedPages, setApprovedPages] = useState<ManagedPage[]>([]);
  const [rejectedPages, setRejectedPages] = useState<ManagedPage[]>([]);
  const [inviteLabel, setInviteLabel] = useState("");
  const [expiresInDays, setExpiresInDays] = useState("7");

  useEffect(() => {
    const storedKey = localStorage.getItem(UPDATES_ADMIN_KEY_STORAGE);
    if (storedKey) setAdminKey(storedKey);
  }, []);

  useEffect(() => {
    if (adminKey.trim()) {
      localStorage.setItem(UPDATES_ADMIN_KEY_STORAGE, adminKey.trim());
    }
  }, [adminKey]);

  const resetDashboard = useCallback(() => {
    setInvites([]);
    setPendingPages([]);
    setApprovedPages([]);
    setRejectedPages([]);
  }, []);

  const adminHeaders = useMemo(
    () => ({
      "Content-Type": "application/json",
      "x-admin-key": adminKey.trim(),
    }),
    [adminKey]
  );

  const loadDashboard = useCallback(async () => {
    if (!adminKey.trim()) return false;

    setIsLoading(true);
    setMessage("");

    try {
      const [invitesResponse, pagesResponse] = await Promise.all([
        fetch(`${apiUrl("facebookAdminListInvites")}?redirectOrigin=${encodeURIComponent(window.location.origin)}`, {
          headers: adminHeaders,
        }),
        fetch(apiUrl("facebookAdminListPages"), {
          headers: adminHeaders,
        }),
      ]);

      const invitesData = await invitesResponse.json();
      const pagesData = await pagesResponse.json();

      if (!invitesResponse.ok) {
        throw new Error(invitesData.error || "Failed to load invite links.");
      }

      if (!pagesResponse.ok) {
        throw new Error(pagesData.error || "Failed to load submitted pages.");
      }

      setInvites(
        Array.isArray(invitesData.invites)
          ? invitesData.invites.filter((invite: InviteRecord) => invite.status === "active")
          : []
      );
      setPendingPages(Array.isArray(pagesData.pendingPages) ? pagesData.pendingPages : []);
      setApprovedPages(Array.isArray(pagesData.approvedPages) ? pagesData.approvedPages : []);
      setRejectedPages(Array.isArray(pagesData.rejectedPages) ? pagesData.rejectedPages : []);
      return true;
    } catch (error) {
      resetDashboard();
      setMessage(error instanceof Error ? error.message : "Failed to load the updates admin dashboard.");
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [adminHeaders, adminKey, resetDashboard]);

  const handleUnlock = async () => {
    if (!adminKey.trim()) {
      setMessage("Enter your admin key first.");
      setIsUnlocked(false);
      return;
    }

    const didLoad = await loadDashboard();
    setIsUnlocked(didLoad);
  };

  const handleLogout = () => {
    localStorage.removeItem(UPDATES_ADMIN_KEY_STORAGE);
    setAdminKey("");
    setInviteLabel("");
    setExpiresInDays("7");
    setCopiedInviteToken("");
    setMessage("");
    setIsUnlocked(false);
    resetDashboard();
  };

  const handleCreateInvite = async () => {
    if (!adminKey.trim()) {
      setMessage("Enter your admin key first.");
      return;
    }

    if (!inviteLabel.trim()) {
      setMessage("Add a label for the invite first.");
      return;
    }

    setIsActing(true);
    setMessage("Creating invite link...");

    try {
      const response = await fetch(apiUrl("facebookAdminCreateInvite"), {
        method: "POST",
        headers: adminHeaders,
        body: JSON.stringify({
          label: inviteLabel.trim(),
          expiresInDays: Number(expiresInDays || 0),
          redirectOrigin: window.location.origin,
        }),
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to create invite.");
      }

      setInviteLabel("");
      setExpiresInDays("7");
      setMessage(`Invite created for ${data.invite?.label || "partner"}.`);
      await loadDashboard();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Failed to create invite.");
    } finally {
      setIsActing(false);
    }
  };

  const handleCopyInvite = async (invite: InviteRecord) => {
    try {
      await navigator.clipboard.writeText(invite.connectUrl);
      setCopiedInviteToken(invite.token);
      setMessage(`Copied invite link for ${invite.label}.`);
      window.setTimeout(() => {
        setCopiedInviteToken((current) => (current === invite.token ? "" : current));
      }, 1800);
    } catch {
      setMessage("Could not copy the invite link on this device.");
    }
  };

  const handleRevokeInvite = async (inviteToken: string) => {
    setIsActing(true);
    setMessage("Revoking invite...");

    try {
      const response = await fetch(apiUrl("facebookAdminRevokeInvite"), {
        method: "POST",
        headers: adminHeaders,
        body: JSON.stringify({ inviteToken }),
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to revoke invite.");
      }

      setInvites((current) => current.filter((invite) => invite.token !== inviteToken));
      setMessage("Invite revoked.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Failed to revoke invite.");
    } finally {
      setIsActing(false);
    }
  };

  const handleApprovePage = async (pageId: string) => {
    setIsActing(true);
    setMessage("Approving Page and syncing latest posts...");

    try {
      const response = await fetch(apiUrl("facebookApprovePage"), {
        method: "POST",
        headers: adminHeaders,
        body: JSON.stringify({ pageId }),
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to approve Page.");
      }

      setMessage(data.syncError ? `Page approved, but sync needs attention: ${data.syncError}` : "Page approved and synced.");
      await loadDashboard();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Failed to approve Page.");
    } finally {
      setIsActing(false);
    }
  };

  const handleRejectPage = async (pageId: string) => {
    setIsActing(true);
    setMessage("Rejecting Page...");

    try {
      const response = await fetch(apiUrl("facebookRejectPage"), {
        method: "POST",
        headers: adminHeaders,
        body: JSON.stringify({ pageId }),
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to reject Page.");
      }

      setMessage("Page rejected.");
      await loadDashboard();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Failed to reject Page.");
    } finally {
      setIsActing(false);
    }
  };

  const handleSyncApprovedPage = async (pageId: string) => {
    setIsActing(true);
    setMessage("Syncing approved Page...");

    try {
      const response = await fetch(apiUrl("facebookSyncConnectedPages"), {
        method: "POST",
        headers: adminHeaders,
        body: JSON.stringify({ pageId }),
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to sync approved Page.");
      }

      setMessage("Approved Page synced.");
      await loadDashboard();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Failed to sync approved Page.");
    } finally {
      setIsActing(false);
    }
  };

  const handleRevokePage = async (pageId: string) => {
    setIsActing(true);
    setMessage("Revoking Page source...");

    try {
      const response = await fetch(apiUrl("facebookDisconnectPage"), {
        method: "POST",
        headers: adminHeaders,
        body: JSON.stringify({ pageId }),
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to revoke Page source.");
      }

      setMessage("Page source revoked.");
      await loadDashboard();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Failed to revoke Page source.");
    } finally {
      setIsActing(false);
    }
  };

  return (
    <IonPage className="city-updates-page">
      <IonHeader className="class-header">
        <IonToolbar className="city-updates-header" />
      </IonHeader>

      <IonContent fullscreen className="city-updates-content web-centered">
        <div className="city-updates-shell">
          <div className="city-updates-hero city-updates-hero-wide">
            <div className="city-updates-card city-updates-page-card updates-portal-card">
              <div className="updates-live-hero">
                <div className="updates-live-pill">
                  <IonIcon icon={logoFacebook} />
                  <span>Updates admin</span>
                </div>
                <h2 className="updates-live-title">Approve trusted Facebook Pages before they go live</h2>
                <p className="updates-live-copy">
                  Create invite links for Bacolod partners, review incoming Page submissions, and control which sources appear in the public city feed.
                </p>
              </div>

              <div className="updates-admin-panel">
                <div className="updates-admin-head">
                  <div>
                    <span className="updates-admin-kicker">Private console</span>
                    <h3>{isUnlocked ? "Approval dashboard" : "Unlock admin dashboard"}</h3>
                  </div>
                  <div className="updates-admin-lock">
                    <IonIcon icon={lockClosedOutline} />
                    <span>{isUnlocked ? "Admin key verified" : "Protected by admin key"}</span>
                  </div>
                </div>

                <div className="updates-admin-controls">
                  <label className="updates-admin-field">
                    <span>Admin key</span>
                    <input
                      type="password"
                      placeholder="Enter admin key"
                      value={adminKey}
                      onChange={(event) => setAdminKey(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          void handleUnlock();
                        }
                      }}
                    />
                  </label>

                  <div className="updates-admin-actions">
                    <button
                      type="button"
                      className="updates-admin-button updates-admin-button-primary"
                      onClick={handleUnlock}
                      disabled={!adminKey.trim() || isLoading}
                    >
                      <IonIcon icon={lockClosedOutline} />
                      <span>{isLoading ? "Checking key..." : "Unlock admin page"}</span>
                    </button>
                    {isUnlocked && (
                      <>
                        <button
                          type="button"
                          className="updates-admin-button"
                          onClick={loadDashboard}
                          disabled={!adminKey.trim() || isLoading || isActing}
                        >
                          <IonIcon icon={refreshOutline} />
                          <span>{isLoading ? "Refreshing..." : "Refresh dashboard"}</span>
                        </button>
                        <button
                          type="button"
                          className="updates-admin-button"
                          onClick={handleLogout}
                          disabled={isLoading || isActing}
                        >
                          <IonIcon icon={closeCircleOutline} />
                          <span>Log out</span>
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {message && <div className="updates-admin-message">{message}</div>}
                {isLoading && <div className="updates-loading-state">{isUnlocked ? "Loading invite links and submitted Pages..." : "Checking admin access..."}</div>}
              </div>

              {isUnlocked && (
                <>
                  <div className="updates-admin-panel">
                    <div className="updates-admin-section-head">
                      <span>Create a partner invite</span>
                      <small>Generate a private connect link for an official Bacolod page owner or partner.</small>
                    </div>

                    <div className="updates-admin-controls">
                      <div className="updates-inline-grid">
                        <label className="updates-admin-field">
                          <span>Invite label</span>
                          <input
                            type="text"
                            placeholder="Bacolod City PIO"
                            value={inviteLabel}
                            onChange={(event) => setInviteLabel(event.target.value)}
                          />
                        </label>

                        <label className="updates-admin-field updates-admin-field-compact">
                          <span>Expires in days</span>
                          <input
                            type="number"
                            min="0"
                            max="365"
                            value={expiresInDays}
                            onChange={(event) => setExpiresInDays(event.target.value)}
                          />
                        </label>
                      </div>

                      <div className="updates-admin-actions">
                        <button
                          type="button"
                          className="updates-admin-button updates-admin-button-primary"
                          onClick={handleCreateInvite}
                          disabled={!adminKey.trim() || isActing}
                        >
                          <IonIcon icon={logoFacebook} />
                          <span>{isActing ? "Working..." : "Create invite link"}</span>
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="updates-portal-grid">
                    <section className="updates-admin-section updates-admin-panel">
                      <div className="updates-admin-section-head">
                        <span>Invite links</span>
                        <small>Share these links with Bacolod partners so they can submit their official Page for approval.</small>
                      </div>

                      <div className="updates-admin-grid">
                        {invites.length ? invites.map((invite) => (
                          <div key={invite.token} className="updates-source-card updates-source-card-selectable">
                            <div className="updates-source-main">
                              <div className="updates-source-fallback">IN</div>
                              <div>
                                <strong>{invite.label}</strong>
                                <span>
                                  {invite.status === "active" ? "Active" : "Revoked"} · Used {invite.useCount} time{invite.useCount === 1 ? "" : "s"}
                                </span>
                              </div>
                            </div>

                            <div className="updates-source-stack">
                              <div className="updates-link-preview">{invite.connectUrl}</div>
                              <div className="updates-source-actions">
                                <button type="button" className="updates-admin-button" onClick={() => handleCopyInvite(invite)}>
                                  <IonIcon icon={copyOutline} />
                                  <span>{copiedInviteToken === invite.token ? "Copied!" : "Copy link"}</span>
                                </button>
                                {invite.status === "active" && (
                                  <button
                                    type="button"
                                    className="updates-icon-button updates-icon-button-danger"
                                    onClick={() => handleRevokeInvite(invite.token)}
                                    disabled={isActing}
                                  >
                                    <IonIcon icon={trashOutline} />
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        )) : (
                          <div className="updates-empty-note">No invite links yet. Create one above to start onboarding a Bacolod Page partner.</div>
                        )}
                      </div>
                    </section>

                    <section className="updates-admin-section updates-admin-panel">
                      <div className="updates-admin-section-head">
                        <span>Pending approvals</span>
                        <small>Review submitted Pages before their posts show up in the public Bacolod feed.</small>
                      </div>

                      <div className="updates-admin-grid">
                        {pendingPages.length ? pendingPages.map((page) => (
                          <div key={page.pageId} className="updates-source-card updates-source-card-selectable">
                            <div className="updates-source-main">
                              {page.pictureUrl ? <img src={page.pictureUrl} alt={page.pageName} /> : <div className="updates-source-fallback">FB</div>}
                              <div>
                                <strong>{page.pageName}</strong>
                                <span>
                                  {(page.category || "Facebook Page") +
                                    (page.submittedByName ? ` · Submitted by ${page.submittedByName}` : "") +
                                    (page.inviteLabel ? ` · ${page.inviteLabel}` : "")}
                                </span>
                                <span>Submitted {formatTimestamp(page.submittedAt)}</span>
                              </div>
                            </div>

                            <div className="updates-source-actions">
                              <button
                                type="button"
                                className="updates-admin-button updates-admin-button-primary"
                                onClick={() => handleApprovePage(page.pageId)}
                                disabled={isActing}
                              >
                                <IonIcon icon={checkmarkCircleOutline} />
                                <span>Approve</span>
                              </button>
                              <button
                                type="button"
                                className="updates-admin-button"
                                onClick={() => handleRejectPage(page.pageId)}
                                disabled={isActing}
                              >
                                <IonIcon icon={closeCircleOutline} />
                                <span>Reject</span>
                              </button>
                            </div>
                          </div>
                        )) : (
                          <div className="updates-empty-note">No Pages are waiting for approval right now.</div>
                        )}
                      </div>
                    </section>
                  </div>

                  <section className="updates-admin-section updates-admin-panel">
                    <div className="updates-admin-section-head">
                      <span>Approved Page sources</span>
                      <small>These are the Facebook Pages currently allowed to feed updates into Bacolod AI.</small>
                    </div>

                    <div className="updates-admin-grid">
                      {approvedPages.length ? approvedPages.map((page) => (
                        <div key={page.pageId} className="updates-source-card">
                          <div className="updates-source-main">
                            {page.pictureUrl ? <img src={page.pictureUrl} alt={page.pageName} /> : <div className="updates-source-fallback">FB</div>}
                            <div>
                              <strong>{page.pageName}</strong>
                              <span>
                                {(page.category || "Facebook Page") +
                                  (page.approvedAt ? ` · Approved ${formatTimestamp(page.approvedAt)}` : "")}
                              </span>
                              <span>
                                {page.lastSyncAt
                                  ? `Last sync ${formatTimestamp(page.lastSyncAt)}`
                                  : "Not synced yet"}
                                {page.lastSyncError ? ` · ${page.lastSyncError}` : ""}
                              </span>
                            </div>
                          </div>

                          <div className="updates-source-actions">
                            <button
                              type="button"
                              className="updates-icon-button"
                              onClick={() => handleSyncApprovedPage(page.pageId)}
                              disabled={isActing}
                            >
                              <IonIcon icon={refreshOutline} />
                            </button>
                            <button
                              type="button"
                              className="updates-icon-button updates-icon-button-danger"
                              onClick={() => handleRevokePage(page.pageId)}
                              disabled={isActing}
                            >
                              <IonIcon icon={trashOutline} />
                            </button>
                          </div>
                        </div>
                      )) : (
                        <div className="updates-empty-note">No approved Pages yet.</div>
                      )}
                    </div>
                  </section>

                  {rejectedPages.length > 0 && (
                    <section className="updates-admin-section updates-admin-panel">
                      <div className="updates-admin-section-head">
                        <span>Rejected Pages</span>
                        <small>Previously reviewed submissions that were not approved.</small>
                      </div>

                      <div className="updates-admin-grid">
                        {rejectedPages.map((page) => (
                          <div key={page.pageId} className="updates-source-card">
                            <div className="updates-source-main">
                              {page.pictureUrl ? <img src={page.pictureUrl} alt={page.pageName} /> : <div className="updates-source-fallback">FB</div>}
                              <div>
                                <strong>{page.pageName}</strong>
                                <span>
                                  {(page.category || "Facebook Page") +
                                    (page.submittedByName ? ` · Submitted by ${page.submittedByName}` : "")}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </section>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default UpdatesAdmin;
